import { v4 as uuidv4 } from 'uuid';
import getUuidByString from 'uuid-by-string';
import { APP_MAX_WIDTH_PX, CONTENT_PATH, isClient, ONE_SECOND_MS, ROOT_SCROLL_ELEMENT_ID } from '../constants';
import * as htmlToImage from 'html-to-image';
import DataLayer, { GTM4Events } from 'services/DataLayer';
// eslint-disable-next-line import/no-extraneous-dependencies
import { animateScroll } from 'react-scroll';


export const numToAlpha = (num: number) => String.fromCharCode(num + 64);

export const getQueryParam = (paramName: string) : string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.has(paramName)
    ? params.get(paramName)
    : null;
};

export const bindViewportListeners = (fn: () => void, shouldInvokeImmediately = false) => {
  window.addEventListener('resize', fn);
  window.addEventListener('orientationchange', fn);

  if (shouldInvokeImmediately) fn();
};

export const getUuid = (text?: string): string => {
  return text ? getUuidByString(text) : uuidv4();
};

export const getWindowWidthPx = () => {
  //Returns the window inner width, unless it's beyond the app_max_width property
  return Math.min(window.innerWidth, APP_MAX_WIDTH_PX);
};

// Mainly created to insert dynamic variables into contentbase strings
export const replaceLabels = (rawStr: string, labels: string[][]) => {
  let output = rawStr;

  for (const [label, value] of labels) {
    output = output.replaceAll(`{${label}}`, value) 
  }

  return output
}

export const shuffleArray = (array: string[]) => { 
  for (let i = array.length - 1; i > 0; i--) { 
    const j = Math.floor(Math.random() * (i + 1)); 
    [array[i], array[j]] = [array[j], array[i]]; 
  } 
  return array; 
}; 

export const onDownloadButtonClick = (buttonText: string, location: string) => {
   const customEvent: GTM4Events['clickCTA'] = {
    link_text: buttonText,
    link_url: '',
    location: location,
  };
  DataLayer.pushClickCTA_GA4(customEvent);
  const node = document.getElementById('screenshot-zone');
  if (node) {
    //The height of the screenshot will match the node. aim for the node that has the content (even if it makes it's parent scroll) NOT the root container
    htmlToImage.toPng(node)
    .then(function (dataUrl: any) {
      const link = document.createElement('a')
      link.download = 'my-plan.png'
      link.href = dataUrl
      link.click()
    })
    .catch(function (err: any) {
      console.error('oops, something went wrong!', err);
    });
  }
}

export const pageScroll = {
  _getRootScrollEl: (element = ROOT_SCROLL_ELEMENT_ID) =>
    isClient ? document.getElementById(element) : null,

  _savedRouteScrollPosition: 0, //Used to keep scroll position when navigating BACK
  _savedScrollPosition: 0, //Used to prevent rapid scroll snapping when elements are added/removed (storyview)

  savePosition: (isRouteChange = false, element = ROOT_SCROLL_ELEMENT_ID) => {
    const rootScrollEl = pageScroll._getRootScrollEl(element);
    if (!rootScrollEl) return;

    const positionToSave = rootScrollEl.scrollTop;

    isRouteChange
      ? (pageScroll._savedRouteScrollPosition = positionToSave)
      : (pageScroll._savedScrollPosition = positionToSave);
  },
  restorePosition: (isRouteChange = false, element = ROOT_SCROLL_ELEMENT_ID) => {
    const rootScrollEl = pageScroll._getRootScrollEl(ROOT_SCROLL_ELEMENT_ID);
    if (!rootScrollEl) return;

    rootScrollEl.scrollTop = isRouteChange
      ? pageScroll._savedRouteScrollPosition
      : pageScroll._savedScrollPosition;
  },

  to: (top = 0, left = 0) => {
    const rootScrollEl = pageScroll._getRootScrollEl();
    if (!rootScrollEl) return;

    rootScrollEl.scrollTo({ top: top, left: left });
  },

  toTopOfId: (id: string) => {
    const targetEle = isClient ? document.getElementById(id) : null;
    if (!targetEle) return;

    const rootScrollEl = pageScroll._getRootScrollEl();
    if (!rootScrollEl) return;

    const scrollTop = rootScrollEl.scrollTop || 0;
    const offsetRect = targetEle.getBoundingClientRect();
    const offsetTop = offsetRect.top;

    animateScroll.scrollTo(offsetTop + scrollTop, {
      containerId: ROOT_SCROLL_ELEMENT_ID,
      duration: ONE_SECOND_MS,
      smooth: 'easeInOutSine',
    });
  },

  toBottomOfId: (element = ROOT_SCROLL_ELEMENT_ID) => {
    const rootScrollEl = pageScroll._getRootScrollEl(element);
    if (!rootScrollEl) return;

    animateScroll.scrollToBottom({
      containerId: element,
      duration: ONE_SECOND_MS,
      smooth: 'easeInOutSine',
    });
  },

  toTop: () => {
    const el = document.getElementById(ROOT_SCROLL_ELEMENT_ID);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    el!.scrollTop = 0;
  },
};

export const getImageFilePath = (filename: string) => {
  return `${CONTENT_PATH}/images/${filename}`;
};